import { DateTime } from "luxon";
import { capitalise } from "../../utils/string";
import { readView } from "../../utils/viewStorage";
import { ViewSchema } from "./types";

const DATE_FORMAT = "yyyy-MM-dd";

const serialiseDate = (value: Date | null): string | null =>
  value ? DateTime.fromJSDate(value).toFormat(DATE_FORMAT) : null;

const deserialiseDate = (raw: string): Date | undefined => {
  const parsed = DateTime.fromFormat(raw, DATE_FORMAT);
  return parsed.isValid ? parsed.toJSDate() : undefined;
};

const serialiseInteger = (value: number | null): string | null =>
  value === null ? null : String(value);

const deserialiseInteger = (raw: string): number | undefined => {
  const parsed = Number(raw);
  return Number.isInteger(parsed) ? parsed : undefined;
};

const readableCheckName = (value: string): string => capitalise(value.replace(/_/g, " "));

interface OptionEntry {
  value: number | string;
  label: string;
}

const findLabel = (options: unknown[], value: unknown): string | undefined =>
  (options as OptionEntry[]).find((option) => option.value === value)?.label;

const isKnownOption = (options: unknown[], value: unknown): boolean =>
  (options as OptionEntry[]).some((option) => option.value === value);

export const screeningView: ViewSchema = {
  dashboard: "screening",
  fields: {
    dateStartedFrom: {
      param: "from",
      default: null,
      serialise: serialiseDate,
      deserialise: deserialiseDate,
      chipLabel: (value: Date) =>
        `Started from: ${DateTime.fromJSDate(value).toFormat("d LLL yyyy")}`,
      inFilterPanel: true,
    },
    dateStartedTo: {
      param: "to",
      default: null,
      serialise: serialiseDate,
      deserialise: deserialiseDate,
      chipLabel: (value: Date) =>
        `Started to: ${DateTime.fromJSDate(value).toFormat("d LLL yyyy")}`,
      inFilterPanel: true,
    },
    // The catalogue this validates against is not fixed: ChecksFilters
    // requests a different name list per tab, and mandate_check further
    // filters it by the tenant's own screening provider. The caller must
    // supply the "checkTypes" option list for whichever tab is actually
    // being restored - see peekRestoredScreeningTab below, which exists
    // precisely because that tab isn't known until resolution has already
    // happened.
    checkType: {
      param: "check",
      default: null,
      serialise: (value: string | null) => value,
      deserialise: (raw) => raw,
      optionsKey: "checkTypes",
      validate: (value, options) => value === null || isKnownOption(options, value),
      chipLabel: (value: string, options) =>
        `Check: ${findLabel(options, value) ?? readableCheckName(value)}`,
      inFilterPanel: true,
    },
    // Topbar UserFilter, not the Filters panel - gets a chip, does not
    // count toward the Filter button's badge. Same pattern as Applications.
    assignedUser: {
      param: "user",
      default: null,
      serialise: serialiseInteger,
      deserialise: deserialiseInteger,
      optionsKey: "users",
      validate: (value, options) => value === null || isKnownOption(options, value),
      chipLabel: (value, options) => `Assigned to: ${findLabel(options, value) ?? value}`,
    },
    search: {
      param: "q",
      default: "",
      serialise: (value: string) => (value === "" ? null : value),
      deserialise: (raw) => raw,
      session: true,
    },
    tab: {
      param: "tab",
      default: 0,
      serialise: (value: number) => (value === 0 ? null : String(value)),
      deserialise: (raw) => {
        const parsed = Number(raw);
        return [0, 1, 2].includes(parsed) ? parsed : undefined;
      },
    },
    sortBy: {
      param: "sort",
      default: "created_on",
      serialise: (value: string) => (value === "created_on" ? null : value),
      deserialise: (raw) => raw,
    },
    sortDesc: {
      param: "dir",
      default: true,
      serialise: (value: boolean) => (value ? null : "asc"),
      deserialise: (raw) => raw !== "asc",
    },
    pageSize: {
      param: "size",
      default: 10,
      serialise: (value: number) => (value === 10 ? null : String(value)),
      deserialise: (raw) => {
        const parsed = Number(raw);
        return Number.isInteger(parsed) ? parsed : undefined;
      },
      optionsKey: "pageSizes",
      validate: (value, options) => (options as number[]).includes(value as number),
    },
  },
};

/**
 * Works out which tab a mount is about to restore, before useDashboardView
 * itself has resolved anything - needed so the page can fetch the right
 * tab's check catalogue in time to validate checkType against it. Mirrors
 * the hook's own "any recognised parameter makes the address bar the sole
 * source" precedence for exactly this one field; everything else about
 * resolution still happens once, inside the hook.
 */
export const peekRestoredScreeningTab = (
  searchParams: URLSearchParams,
  tenantId: number | undefined,
  userId: number | undefined
): number => {
  const isAddressSourced = Object.values(screeningView.fields).some((field) =>
    searchParams.has(field.param)
  );

  const raw = isAddressSourced
    ? searchParams.get("tab")
    : userId !== undefined
    ? readView("screening", tenantId, userId).tab
    : undefined;

  if (raw === undefined || raw === null) return 0;
  const parsed = Number(raw);
  return [0, 1, 2].includes(parsed) ? parsed : 0;
};

/** The client-side check-name catalogue ChecksFilters requests per tab, before the tenant's provider narrows it further. */
export const SCREENING_CHECK_NAMES_BY_TAB: Record<number, string[]> = {
  0: [
    "address_verification_check",
    "pep_%26_sanctions_check",
    "adverse_media_check",
    "entity_adverse_media_check",
    "sanctions_check",
    "ln_screening_check",
    "ln_screening_check_entity",
  ],
  1: [
    "address_verification_check",
    "pep_%26_sanctions_check",
    "adverse_media_check",
    "sanctions_check",
    "ln_screening_check",
  ],
  2: [
    "address_verification_check",
    "pep_%26_sanctions_check",
    "entity_adverse_media_check",
    "sanctions_check",
    "ln_screening_check_entity",
  ],
};
